// ecommerce-frontend/src/pages/ForgotPasswordPage.jsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle, Key, X } from 'lucide-react';
import { authAPI } from '../services/api';

// Styles d'animation CSS
const styles = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
  }

  @keyframes wiggle {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-5deg); }
    75% { transform: rotate(5deg); }
  }

  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.5); }
    50% { box-shadow: 0 0 40px rgba(34, 197, 94, 0.8); }
  }

  .shake-animation {
    animation: shake 0.5s ease-in-out;
  }

  .wiggle-animation {
    animation: wiggle 0.5s ease-in-out;
  }

  .pulse-glow-animation {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .icon-container {
    transition: transform 0.3s ease;
  }

  .icon-container:hover {
    transform: scale(1.1) rotate(5deg);
  }
`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shakeIcon, setShakeIcon] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      setShakeIcon(true);
      setTimeout(() => setShakeIcon(false), 500);
      return;
    }

    setLoading(true);

    try {
      await authAPI.forgotPassword({ email });
      setStep(2);
    } catch (err) {
      if (err.response && err.response.data) {
        const errorMsg = err.response.data.error || err.response.data.detail;
        if (errorMsg && errorMsg.includes('not found')) {
          setError('Aucun compte associé à cet email');
        } else {
          setError(errorMsg || 'Une erreur est survenue');
        }
      } else {
        setError('Impossible de se connecter au serveur. Vérifiez votre connexion internet.');
      }
      setShakeIcon(true);
      setTimeout(() => setShakeIcon(false), 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-400/10 rounded-full blur-3xl"></div>

        <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl max-w-md w-full p-8 border border-white/20 relative z-10">
          {step === 1 ? (
            <>
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl mb-4 shadow-xl icon-container ${shakeIcon ? 'shake-animation' : ''}`}>
                  <Key size={36} className="text-white" />
                </div>
                <h2 className="text-4xl font-bold text-white mb-2">Mot de passe oublié ?</h2>
                <p className="text-gray-300">
                  Entrez votre email pour recevoir un lien de réinitialisation
                </p>
              </div>

              {error && (
                <div className="bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl p-4 mb-6 flex items-start justify-between shake-animation">
                  <div className='flex items-start'>
                    <AlertCircle size={20} className="text-red-300 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-red-100 text-sm">{error}</span>
                  </div>
                  <button onClick={() => setError('')} className="p-1 rounded-full text-red-300 hover:bg-red-500/30 transition-colors flex-shrink-0">
                    <X size={16} />
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Adresse email
                  </label>
                  <div className="relative">
                    <Mail className={`absolute left-4 top-4 text-gray-400 ${error ? 'wiggle-animation' : ''}`} size={20} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError('');
                      }}
                      className={`w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all ${
                        error ? 'border-red-400' : 'border-white/20'
                      }`}
                      placeholder="votre.email@exemple.com"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl hover:shadow-2xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Mail size={22} className="mr-2" />
                      Envoyer le lien
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center text-sm text-gray-300 hover:text-white transition"
                >
                  <ArrowLeft size={16} className="mr-1" />
                  Retour à la connexion
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-3xl mb-4 shadow-xl pulse-glow-animation">
                  <CheckCircle size={36} className="text-white" />
                </div>
                <h2 className="text-4xl font-bold text-white mb-2">Email envoyé !</h2>
                <p className="text-gray-300 mt-4">
                  Un lien de réinitialisation a été envoyé à :
                </p>
                <p className="text-orange-400 font-semibold mt-2">{email}</p>
              </div>

              <div className="bg-orange-500/20 backdrop-blur-md border border-orange-400/30 rounded-2xl p-4 mb-6">
                <p className="text-sm text-white font-semibold mb-2">
                  Instructions :
                </p>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li className="flex items-start">
                    <span className="text-orange-400 mr-2">•</span>
                    <span>Vérifiez votre boîte de réception</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-400 mr-2">•</span>
                    <span>Cliquez sur le lien dans l'email</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-400 mr-2">•</span>
                    <span>Créez un nouveau mot de passe</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-400 mr-2">•</span>
                    <span>Le lien expire dans 24 heures</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl hover:shadow-2xl transform hover:scale-[1.02]"
                >
                  Retour à la connexion
                </button>

                <button
                  onClick={() => setStep(1)}
                  className="w-full bg-white/10 backdrop-blur-md text-white py-4 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all border border-white/20"
                >
                  Renvoyer l'email
                </button>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-300">
                  Vous n'avez pas reçu l'email ?{' '}
                  <span className="text-gray-400">Vérifiez vos spams</span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}